<template>
    <div :class="['slider-container']" :style="{height: `${height+topPadding}px`, paddingTop: `${topPadding}px`}">

        <template v-for="(bar, i) in bars">
            <div :key="i" :class="['bar']" :style="bar.barStyle"></div>
            <div :key="`${i}a`" :class="['bottom-label']" v-if="barLabel(bar,i)" :style="bar.labelStyle">
                {{ bar.label }}
            </div>
        </template>

        <input
            type="range"
            :min="min"
            :max="max"
            :step="step"
            :style="{height: `${height}px`}"
            v-model="model"
            :class="['slider', {'background-bars': withBackground}]"
            @mousedown="pressed = true"
            @mouseup="pressed = false"
            @touchstart="pressed = true"
            @touchend="pressed = false"
        >

        <div class="label left" v-if="label">{{ label }}</div>
        <div class="label right" v-if="label">{{ rightLabel ? rightLabel : round(model) }} <span
            v-if="units">{{ units }}</span></div>
        <div :class="['thumb', {'pressed': pressed}]" :style="thumbStyle"></div>
        <div :class="['stick', {'pressed': pressed}]" :style="stickStyle"></div>


    </div>
</template>

<script>
export default {
    name: "KimsaSlider",
    props: {
        decimals: {
            type: Number,
            default: 2
        },
        label: {
            type: String,
            default: 'Estatura'
        },
        labelsInterval: {
            type: Number,
            default: .5
        },
        units: {
            type: String,
            default: 'cm'
        },
        stickWith: {
            type: Number,
            default: 2
        },
        thumbRadius: {
            type: Number,
            default: 4
        },
        color: {
            type: String,
            default: 'secondary'
        },
        value: {
            type: [Number, String],
            default: 0
        },
        step: {
            type: Number,
            default: 0.1
        },
        height: {
            type: Number,
            default: 30
        },
        min: {
            type: Number,
            default: 0
        },
        max: {
            type: Number,
            default: 2
        },
        noLabelInterval: {
            type: Boolean,
            default: false,
        },
        rightLabel: {
            type: String,
            default: '',
        },
        withBackground: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            model: 0,
            pressed: false,
        }
    },
    computed: {
        topPadding() {
            return 22
        },
        thumbStyle() {

            const vm = this
            const borderRadius = `${vm.thumbRadius}px`
            const diameter = `${vm.thumbRadius * 2}px`
            const top = `${vm.topPadding - vm.thumbRadius}px`
            const color = vm.baseColor
            const border = `${vm.stickWith}px solid ${color}`

            return {
                zIndex: '0',
                border,
                height: diameter,
                width: diameter,
                borderRadius,
                borderColor: color,
                top: top,
                left: `calc(${vm.thumbPosition}% - ${vm.thumbRadius}px)`
            }
        },
        stickStyle() {

            const vm = this
            const height = `${vm.height - (vm.thumbRadius)}px`
            const width = `${vm.stickWith}px`
            const bottom = 0
            const color = vm.baseColor

            return {
                zIndex: '0',
                height: height,
                width: width,
                backgroundColor: color,
                bottom: bottom,
                left: `calc(${vm.thumbPosition}% - ${vm.stickWith / 2}px)`
            }
        },
        baseColor() {
            const vm = this
            return vm.$vuetify.theme.themes.light[vm.color] || vm.color
        },
        thumbPosition() {
            const vm = this
            return vm.percentFromValue(vm.model)
        },
        bars() {

            const vm = this
            let bars = []
            let count = 0
            const baseHeight = vm.height - vm.thumbRadius
            const width = vm.stickWith * 0.5
            const labelWidth = 40

            for (let i = vm.min; i <= vm.max; i += vm.step) {

                const labeled = count === 0 || count >= vm.labelsInterval
                const active = vm.round(i) === vm.round(vm.model)

                bars.push({
                    label: labeled ? `${vm.round(i)}` : '',
                    active,
                    barStyle: {
                        zIndex: 1,
                        backgroundColor: vm.baseColor,
                        width: `${width}px`,
                        left: `calc(${vm.percentFromValue(i)}% - ${width / 2}px)`,
                        height: labeled ? `${baseHeight}px` : `${baseHeight * 0.5}px`
                    },
                    labelStyle: {
                        color: vm.baseColor,
                        fontWeight: active ? 'bold' : '400',
                        width: `${labelWidth}px`,
                        left: `${vm.percentFromValue(i)}%`
                    }
                })

                count = labeled ? vm.step : count + vm.step
            }

            bars.push({
                label: '',
                active: vm.model === vm.max,
                barStyle: {
                    zIndex: 1,
                    backgroundColor: vm.baseColor,
                    width: `${width}px`,
                    left: `calc(${vm.percentFromValue(vm.max)}% - ${width / 2}px)`,
                    height: `${baseHeight * 0.5}px`
                }
            })

            return bars

        },
    },
    mounted() {
        const vm = this
        vm.setDefaults()
    },
    methods: {
        percentFromValue(value) {
            const vm = this
            const totalSteps = vm.max - vm.min
            return (((value - vm.min) * 100) / totalSteps)
        },
        round(value) {
            const vm = this
            const zeros = '0'.repeat(vm.decimals)
            const factor = Number(`1${zeros}`)

            //fixme round not working as espected, always rounds to 1 position
            return Math.round((Number(value) + Number.EPSILON) * factor) / factor
        },
        barLabel(bar, i) {
            const vm = this
            let index = i
            if(vm.min !== 0)  index++
            if (vm.noLabelInterval) return index === vm.min || index === vm.max || index === vm.round(vm.model)
            return !!bar.label
        },
        setDefaults() {
            const vm = this
            if (vm.value) vm.model = vm.value
        }
    },
    watch: {
        model(val) {
            this.$emit('change', val)
        },
        value() {
            this.setDefaults()
        }
    }
}
</script>

<style scoped>

.label {
    position: absolute;
    top: -4px;
    font-size: 15px;
    color: #707070;
}

.label.left {
    left: 0
}

.label.right {
    right: 0
}

.bar {
    pointer-events: none;
    position: absolute;
    transform-origin: bottom center;
    bottom: 0
}

.bottom-label {
    pointer-events: none;
    text-align: center;
    position: absolute;
    transform-origin: center center;
    transform: translate(-50%, 0);;
    font-size: .7em;
    bottom: -18px
}

.stick,
.thumb {
    position: absolute;
    pointer-events: none;
    z-index: 1;
    transition: .3s transform;
}

.stick {
    transform-origin: bottom center;
}

.thumb {
    transform-origin: center center;
}

.stick.pressed {
    transform: scaleY(1.2);
}

.thumb.pressed {
    transform: scale(1.1) translateY(-5px);
}

.slider-container {
    position: relative;
    width: 100%;
    padding-top: 15px;
    background-color: transparent;
    margin-bottom: 5px;
}

/* The slider itself */
.slider.primary,
.slider.secondary,
.slider.accent,
.slider.info,
.slider {
    -webkit-appearance: none; /* Override default CSS styles */
    appearance: none;
    width: 100%; /* Full-width */
    height: 25px; /* Specified height */
    background-color: transparent !important; /* Grey background */
    outline: none; /* Remove outline */
    opacity: 0.7; /* Set transparency (for mouse-over effects on hover) */
    -webkit-transition: .2s; /* 0.2 seconds transition on hover */
    transition: opacity .2s;
}


.slider::-webkit-slider-thumb {
    -webkit-appearance: none; /* Override default look */
    position: relative;
    z-index: 10;
    appearance: none;
    width: 20px; /* Set a specific slider handle width */
    height: 40px; /* Slider handle height */
    background-color: transparent !important;
    border-color: transparent !important;
    cursor: pointer; /* Cursor on hover */

}

.slider::-moz-range-thumb {
    width: 20px; /* Set a specific slider handle width */
    height: 40px; /* Slider handle height */
    background-color: transparent !important;
    border-color: transparent !important;
    cursor: pointer; /* Cursor on hover */
}

.background-bars {
    position: absolute;
    bottom: -2px;
    background: rgb(213, 254, 70);
    background: linear-gradient(0deg, rgba(7, 133, 113, .5) 0%, rgba(213, 254, 70, 0.5) 100%);
}


</style>